import { useEffect, useRef, useCallback, useState, memo } from "react";
import { formatDuration } from "../lib/utils/time";
import { useTranslation } from "react-i18next";
import { getThemeColors } from "../lib/theme";
import { useFullscreen } from "../hooks/useFullscreen";
import { FullscreenButton } from "./FullscreenButton";

interface TimelineItem {
  text: string;
  duration: number;
  type?: "highlight";
}

interface TimelinePlayer {
  name: string;
  articles: TimelineItem[];
}

interface TimelineProps {
  players: TimelinePlayer[];
  isDark: boolean;
}

const DARK_PALETTE = {
  BG_CANVAS: "#18181B",
  BG_ARTICLE_BOX: "#27272A",
  TEXT_PRIMARY: "#A1A1AA",
  TEXT_PLAYER_NAME: "#FAFAFA",
  TEXT_PLAYER_NUMBER: "#71717A",
  TIMELINE_LINE: "#3F3F46",
  MARKER_FILL: "#FAFAFA",
  ACCENT_HIGHLIGHT_BG: "#164E63",
  ACCENT_HIGHLIGHT_TEXT: "#A5F3FC",
  TIME_AXIS_LINE: "#3F3F46",
  TIME_AXIS_TEXT: "#71717A",
  SCROLLBAR_BG: "#27272A",
  SCROLLBAR_THUMB: "#3F3F46",
  SCROLLBAR_THUMB_HOVER: "#52525B",
  ARTICLE_HOVER_BG: "#3F3F46",
  ARTICLE_HOVER_BORDER: "#A1A1AA",
  ARTICLE_HOVER_TEXT: "#FAFAFA",
  HIGHLIGHT_ARTICLE_HOVER_BG: "#155E75",
  HIGHLIGHT_ARTICLE_HOVER_BORDER: "#22D3EE",
  HIGHLIGHT_ARTICLE_HOVER_TEXT: "#CFFAFE",
  ARTICLE_BORDER: "#3F3F46",
};

const LIGHT_PALETTE = {
  BG_CANVAS: "#FAFAFA",
  BG_ARTICLE_BOX: "#FFFFFF",
  TEXT_PRIMARY: "#52525B",
  TEXT_PLAYER_NAME: "#18181B",
  TEXT_PLAYER_NUMBER: "#A1A1AA",
  TIMELINE_LINE: "#D4D4D8",
  MARKER_FILL: "#18181B",
  ACCENT_HIGHLIGHT_BG: "#F3F4F6",
  ACCENT_HIGHLIGHT_TEXT: "#111827",
  TIME_AXIS_LINE: "#D4D4D8",
  TIME_AXIS_TEXT: "#A1A1AA",
  SCROLLBAR_BG: "#F4F4F5",
  SCROLLBAR_THUMB: "#D4D4D8",
  SCROLLBAR_THUMB_HOVER: "#A1A1AA",
  ARTICLE_HOVER_BG: "#F4F4F5",
  ARTICLE_HOVER_BORDER: "#71717A",
  ARTICLE_HOVER_TEXT: "#18181B",
  HIGHLIGHT_ARTICLE_HOVER_BG: "#E5E7EB",
  HIGHLIGHT_ARTICLE_HOVER_BORDER: "#6B7280",
  HIGHLIGHT_ARTICLE_HOVER_TEXT: "#111827",
  ARTICLE_BORDER: "#D4D4D8",
};

function getPalette(isDark: boolean) {
  return isDark ? DARK_PALETTE : LIGHT_PALETTE;
}

const FONT_FAMILY_SANS = `system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
const PLAYER_NAME_FONT_SIZE = 16;
const PLAYER_NUMBER_FONT_SIZE = 16;
const ARTICLE_TEXT_SIZE = 14;
const TIME_AXIS_FONT_SIZE = 12;
const ARTICLE_TEXT_FONT = `${ARTICLE_TEXT_SIZE}px ${FONT_FAMILY_SANS}`;
const PLAYER_NAME_FONT_BOLD = `bold ${PLAYER_NAME_FONT_SIZE}px ${FONT_FAMILY_SANS}`;
const PLAYER_NUMBER_FONT_REGULAR = `${PLAYER_NUMBER_FONT_SIZE}px ${FONT_FAMILY_SANS}`;
const TIME_AXIS_FONT = `${TIME_AXIS_FONT_SIZE}px ${FONT_FAMILY_SANS}`;

const PADDING = 25;
const SIDEBAR_WIDTH = 180;
const PLAYER_ROW_HEIGHT = 70;
const ARTICLE_BOX_PADDING_X = 12;
const ARTICLE_BOX_PADDING_Y = 8;
const ARTICLE_BOX_HEIGHT = ARTICLE_TEXT_SIZE + 2 * ARTICLE_BOX_PADDING_Y;
const ARTICLE_BOX_RADIUS = 8;
const ARTICLE_MARKER_RADIUS = 4;
const TIMELINE_Y_OFFSET_IN_ROW = 45;
const ARTICLE_BOX_BOTTOM_MARGIN_TO_MARKER = 6;
const TIME_AXIS_AREA_HEIGHT = 40;
const TIME_AXIS_PADDING_TOP_INTERNAL = 10;
const TIME_AXIS_MAJOR_TICK_HEIGHT = 8;
const TIME_AXIS_MINOR_TICK_HEIGHT = 4;
const TIME_AXIS_TEXT_OFFSET_Y = 15;
const HORIZONTAL_SCROLLBAR_AREA_HEIGHT = 20;
const HORIZONTAL_SCROLLBAR_HEIGHT = 8;
const HORIZONTAL_SCROLLBAR_RADIUS = 4;
const ZOOM_FACTOR = 1.25;
const MIN_PIXELS_PER_SECOND = 0.01;

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radiusConfig: number | { tl?: number; tr?: number; br?: number; bl?: number },
  fill = false,
  stroke = true,
) {
  let radius = { tl: 0, tr: 0, br: 0, bl: 0 };
  if (typeof radiusConfig === "number") {
    radius = {
      tl: radiusConfig,
      tr: radiusConfig,
      br: radiusConfig,
      bl: radiusConfig,
    };
  } else if (typeof radiusConfig === "object" && radiusConfig !== null) {
    radius = { ...radius, ...radiusConfig };
  }
  ctx.beginPath();
  ctx.moveTo(x + radius.tl, y);
  ctx.lineTo(x + width - radius.tr, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
  ctx.lineTo(x + width, y + height - radius.br);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - radius.br,
    y + height,
  );
  ctx.lineTo(x + radius.bl, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
  ctx.lineTo(x, y + radius.tl);
  ctx.quadraticCurveTo(x, y, x + radius.tl, y);
  ctx.closePath();
  if (fill) {
    ctx.fill();
  }
  if (stroke) {
    ctx.stroke();
  }
}

function getTimeScaleConfig(pixelsPerSecond: number) {
  const scales = [
    {
      minPps: 0,
      maxPps: 0.04,
      majorInterval: 3600,
      minorTicks: 6,
      suffix: "h",
      divisor: 3600,
    },
    {
      minPps: 0.04,
      maxPps: 0.1,
      majorInterval: 1800,
      minorTicks: 6,
      suffix: "m",
      divisor: 60,
    },
    {
      minPps: 0.1,
      maxPps: 0.22,
      majorInterval: 600,
      minorTicks: 10,
      suffix: "m",
      divisor: 60,
    },
    {
      minPps: 0.22,
      maxPps: 0.45,
      majorInterval: 300,
      minorTicks: 5,
      suffix: "m",
      divisor: 60,
    },
    {
      minPps: 0.45,
      maxPps: 2.2,
      majorInterval: 60,
      minorTicks: 6,
      suffix: "m",
      divisor: 60,
    },
    {
      minPps: 2.2,
      maxPps: 4.5,
      majorInterval: 30,
      minorTicks: 3,
      suffix: "s",
      divisor: 1,
    },
    {
      minPps: 4.5,
      maxPps: 9,
      majorInterval: 10,
      minorTicks: 5,
      suffix: "s",
      divisor: 1,
    },
    {
      minPps: 9,
      maxPps: 17,
      majorInterval: 5,
      minorTicks: 5,
      suffix: "s",
      divisor: 1,
    },
    {
      minPps: 17,
      maxPps: 33,
      majorInterval: 2,
      minorTicks: 2,
      suffix: "s",
      divisor: 1,
    },
    {
      minPps: 33,
      maxPps: Infinity,
      majorInterval: 1,
      minorTicks: 5,
      suffix: "s",
      divisor: 1,
    },
  ];

  for (const scale of scales) {
    if (pixelsPerSecond >= scale.minPps && pixelsPerSecond < scale.maxPps) {
      return scale;
    }
  }

  return { majorInterval: 5, minorTicks: 5, suffix: "s", divisor: 1 };
}

interface HoveredArticle {
  playerIndex: number;
  articleIndex: number;
  articleRef: TimelineItem;
}

function MobileTimeline({ players, isDark }: TimelineProps) {
  const { t } = useTranslation();
  const theme = getThemeColors(isDark);
  const [collapsedPlayers, setCollapsedPlayers] = useState<Set<number>>(
    () => new Set(),
  );

  const togglePlayer = (playerIndex: number) => {
    setCollapsedPlayers((current) => {
      const next = new Set(current);
      if (next.has(playerIndex)) {
        next.delete(playerIndex);
      } else {
        next.add(playerIndex);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4 sm:hidden" data-testid="timeline-mobile">
      {players.map((player, playerIndex) => {
        const isExpanded = !collapsedPlayers.has(playerIndex);
        const pathId = `timeline-mobile-path-${playerIndex}`;

        return (
          <section
            key={`${player.name}-${playerIndex}`}
            data-testid={`timeline-mobile-player-${playerIndex}`}
            className={`rounded-xl border p-4 ${theme.card}`}
          >
            <h3>
              <button
                type="button"
                aria-controls={pathId}
                aria-expanded={isExpanded}
                aria-label={t(
                  isExpanded
                    ? "components.timeline.collapsePlayer"
                    : "components.timeline.expandPlayer",
                  { player: player.name },
                )}
                data-testid={`timeline-mobile-player-toggle-${playerIndex}`}
                onClick={() => togglePlayer(playerIndex)}
                className={`flex w-full cursor-pointer items-center justify-between gap-3 text-left text-sm font-medium ${theme.text}`}
              >
                <span className="min-w-0 truncate">
                  <span className={theme.textMuted}>{playerIndex + 1}.</span>{" "}
                  {player.name}
                </span>
                <ChevronDownIcon isExpanded={isExpanded} />
              </button>
            </h3>

            {isExpanded ? (
              <ol id={pathId} className="mt-4 space-y-3">
                {player.articles.map((article, articleIndex) => {
                  const isLastArticle =
                    articleIndex === player.articles.length - 1;
                  const isHighlight = article.type === "highlight";

                  return (
                    <li
                      key={`${article.text}-${article.duration}-${articleIndex}`}
                      className="grid grid-cols-[1rem_minmax(0,1fr)] items-stretch gap-3"
                    >
                      <div
                        aria-hidden="true"
                        className="relative flex h-full justify-center"
                      >
                        <span
                          className={`mt-3 size-2 shrink-0 rounded-full ${theme.bgAccent}`}
                        />
                        {!isLastArticle ? (
                          <span
                            className={`absolute bottom-[-0.75rem] top-5 border-l ${theme.border}`}
                          />
                        ) : null}
                      </div>

                      <div
                        className={`min-w-0 rounded-lg border px-3 py-2 ${
                          isHighlight
                            ? `${theme.accentBg} ${theme.accentText} ${theme.border}`
                            : `${theme.bgSecondary} ${theme.text} ${theme.border}`
                        }`}
                      >
                        <div className="mb-1.5 flex items-center justify-between gap-2">
                          <span
                            className={`text-xs tabular-nums ${theme.textMuted}`}
                          >
                            {formatDuration(article.duration)}
                          </span>
                          {isHighlight ? (
                            <span className="text-[0.6875rem] font-medium uppercase tracking-wide">
                              {t("components.timeline.target")}
                            </span>
                          ) : null}
                        </div>
                        <p className="break-words text-sm font-medium leading-5">
                          {article.text}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function ChevronDownIcon({ isExpanded }: { isExpanded: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className={`size-4 shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="m6 9 6 6 6-6"
      />
    </svg>
  );
}

function TimelineComponent({ players, isDark }: TimelineProps) {
  const { t } = useTranslation();
  const theme = getThemeColors(isDark);
  const { fullscreenRef, isFullscreen, toggleFullscreen } =
    useFullscreen<HTMLDivElement>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pixelsPerSecond, setPixelsPerSecond] = useState(50);
  const [scrollOffsetX, setScrollOffsetX] = useState(0);
  const [scrollOffsetY, setScrollOffsetY] = useState(0);
  const [hoveredArticles, setHoveredArticles] = useState<HoveredArticle[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState<
    "main" | "sidebar" | "scrollbar" | null
  >(null);
  const dragStartRef = useRef({
    x: 0,
    y: 0,
    scrollX: 0,
    scrollY: 0,
    thumbStartX: 0,
  });
  const initialZoomSetRef = useRef(false);
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const isMouseOverScrollbarThumbRef = useRef(false);

  const PALETTE = getPalette(isDark);

  const calculateBounds = useCallback(() => {
    if (!canvasRef.current || players.length === 0) {
      return {
        minContentStartPx: 0,
        maxContentEndPx: 0,
        contentSpanTotalPixels: 0,
        timelineMinScrollOffsetX: 0,
        timelineMaxScrollOffsetX: 0,
        maxVerticalScrollOffset: 0,
      };
    }

    const canvas = canvasRef.current;
    const logicalWidth = canvas.width / (window.devicePixelRatio || 1);
    const logicalHeight = canvas.height / (window.devicePixelRatio || 1);
    const M_AREA_X = PADDING + SIDEBAR_WIDTH + PADDING;
    const M_AREA_W = logicalWidth - M_AREA_X - PADDING;
    const M_AREA_H = logicalHeight - 2 * PADDING;
    const mainPanelContentHeightForPlayerRows =
      M_AREA_H - TIME_AXIS_AREA_HEIGHT - HORIZONTAL_SCROLLBAR_AREA_HEIGHT;

    const ctx = canvas.getContext("2d");
    let minContentStartPx = Infinity;
    let maxContentEndPx = -Infinity;

    if (ctx) {
      ctx.font = ARTICLE_TEXT_FONT;
      players.forEach((player) => {
        player.articles.forEach((article) => {
          const textMetrics = ctx.measureText(article.text);
          const articleBoxWidthInPixels =
            textMetrics.width + 2 * ARTICLE_BOX_PADDING_X;
          const articleCenterInPixels = article.duration * pixelsPerSecond;

          const articleStartPx =
            articleCenterInPixels - articleBoxWidthInPixels / 2;
          const articleEndPx =
            articleCenterInPixels + articleBoxWidthInPixels / 2;

          if (articleStartPx < minContentStartPx)
            minContentStartPx = articleStartPx;
          if (articleEndPx > maxContentEndPx) maxContentEndPx = articleEndPx;
        });
      });
    }

    if (minContentStartPx === Infinity) minContentStartPx = 0;
    if (maxContentEndPx === -Infinity) maxContentEndPx = 0;

    const contentSpanTotalPixels =
      players.length === 0 || players.every((p) => p.articles.length === 0)
        ? 0
        : maxContentEndPx - minContentStartPx;

    const visibleTimelineTrackWidth = M_AREA_W;

    let timelineMinScrollOffsetX: number;
    let timelineMaxScrollOffsetX: number;

    if (contentSpanTotalPixels <= visibleTimelineTrackWidth) {
      timelineMaxScrollOffsetX = -minContentStartPx;
      timelineMinScrollOffsetX = -minContentStartPx;
    } else {
      timelineMaxScrollOffsetX = -minContentStartPx;
      timelineMinScrollOffsetX = visibleTimelineTrackWidth - maxContentEndPx;
    }

    const totalScrollablePlayerContentHeight =
      players.length * PLAYER_ROW_HEIGHT;
    const maxVerticalScrollOffset = -Math.max(
      0,
      totalScrollablePlayerContentHeight - mainPanelContentHeightForPlayerRows,
    );

    return {
      minContentStartPx,
      maxContentEndPx,
      contentSpanTotalPixels,
      timelineMinScrollOffsetX,
      timelineMaxScrollOffsetX,
      maxVerticalScrollOffset,
    };
  }, [players, pixelsPerSecond]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || players.length === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const logicalWidth = canvas.width / dpr;
    const logicalHeight = canvas.height / dpr;

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    ctx.fillStyle = PALETTE.BG_CANVAS;
    ctx.fillRect(0, 0, logicalWidth, logicalHeight);

    const S_AREA_X = PADDING;
    const S_AREA_Y = PADDING;
    const S_AREA_W = SIDEBAR_WIDTH;
    const S_AREA_H = logicalHeight - 2 * PADDING;
    const M_AREA_X = PADDING + SIDEBAR_WIDTH + PADDING;
    const M_AREA_Y = PADDING;
    const M_AREA_W = logicalWidth - M_AREA_X - PADDING;
    const M_AREA_H = logicalHeight - 2 * PADDING;
    const mainPanelContentHeightForPlayerRows =
      M_AREA_H - TIME_AXIS_AREA_HEIGHT - HORIZONTAL_SCROLLBAR_AREA_HEIGHT;
    const timelineTrackScreenStartX = M_AREA_X;
    const timelineTrackScreenEndX = M_AREA_X + M_AREA_W;

    ctx.save();
    ctx.beginPath();
    ctx.rect(S_AREA_X, S_AREA_Y, S_AREA_W, S_AREA_H);
    ctx.clip();
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";

    for (let i = 0; i < players.length; i++) {
      const baseTimelineYInMainArea =
        M_AREA_Y + i * PLAYER_ROW_HEIGHT + TIMELINE_Y_OFFSET_IN_ROW;
      const playerNameDisplayY = baseTimelineYInMainArea + scrollOffsetY;

      if (
        playerNameDisplayY + PLAYER_NAME_FONT_SIZE / 2 < S_AREA_Y ||
        playerNameDisplayY - PLAYER_NAME_FONT_SIZE / 2 > S_AREA_Y + S_AREA_H
      )
        continue;

      const playerNumberText = `${i + 1}. `;
      const playerName = players[i].name;
      let currentX = S_AREA_X + PADDING / 2;

      ctx.font = PLAYER_NUMBER_FONT_REGULAR;
      ctx.fillStyle = PALETTE.TEXT_PLAYER_NUMBER;
      ctx.fillText(playerNumberText, currentX, playerNameDisplayY);
      currentX += ctx.measureText(playerNumberText).width;

      ctx.font = PLAYER_NAME_FONT_BOLD;
      ctx.fillStyle = PALETTE.TEXT_PLAYER_NAME;

      const availableNameWidth = S_AREA_X + S_AREA_W - currentX - PADDING / 2;
      let nameToDraw = playerName;
      if (ctx.measureText(playerName).width > availableNameWidth) {
        let truncatedName = "";
        for (const char of playerName) {
          if (
            ctx.measureText(truncatedName + char + "...").width >
            availableNameWidth
          )
            break;
          truncatedName += char;
        }
        nameToDraw = truncatedName + "...";
      }
      ctx.fillText(nameToDraw, currentX, playerNameDisplayY);
    }
    ctx.restore();

    ctx.save();
    ctx.beginPath();
    ctx.rect(M_AREA_X, M_AREA_Y, M_AREA_W, mainPanelContentHeightForPlayerRows);
    ctx.clip();

    players.forEach((player, playerIndex) => {
      const playerRowTopUnscrolled = M_AREA_Y + playerIndex * PLAYER_ROW_HEIGHT;
      const playerRowTopScrolled = playerRowTopUnscrolled + scrollOffsetY;

      if (
        playerRowTopScrolled + PLAYER_ROW_HEIGHT < M_AREA_Y ||
        playerRowTopScrolled > M_AREA_Y + mainPanelContentHeightForPlayerRows
      )
        return;

      const timelineLineYScrolled =
        playerRowTopUnscrolled + scrollOffsetY + TIMELINE_Y_OFFSET_IN_ROW;

      ctx.strokeStyle = PALETTE.TIMELINE_LINE;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(timelineTrackScreenStartX, timelineLineYScrolled);
      ctx.lineTo(timelineTrackScreenEndX, timelineLineYScrolled);
      ctx.stroke();
      ctx.setLineDash([]);

      const articleBoxActualYScrolled =
        timelineLineYScrolled -
        ARTICLE_MARKER_RADIUS -
        ARTICLE_BOX_BOTTOM_MARGIN_TO_MARKER -
        ARTICLE_BOX_HEIGHT;

      player.articles.forEach((article, articleIndex) => {
        if (
          hoveredArticles.some(
            (d) =>
              d.playerIndex === playerIndex && d.articleIndex === articleIndex,
          )
        ) {
          return;
        }

        ctx.font = ARTICLE_TEXT_FONT;
        const textMetrics = ctx.measureText(article.text);
        const articleBoxWidth = textMetrics.width + 2 * ARTICLE_BOX_PADDING_X;
        const articleCenterPixelXOffsetFromOrigin =
          article.duration * pixelsPerSecond;
        const articleBoxDrawX =
          timelineTrackScreenStartX +
          scrollOffsetX +
          articleCenterPixelXOffsetFromOrigin -
          articleBoxWidth / 2;
        const articleBoxDrawY = articleBoxActualYScrolled;

        if (
          articleBoxDrawX + articleBoxWidth < M_AREA_X ||
          articleBoxDrawX > M_AREA_X + M_AREA_W
        ) {
          return;
        }

        ctx.fillStyle =
          article.type === "highlight"
            ? PALETTE.ACCENT_HIGHLIGHT_BG
            : PALETTE.BG_ARTICLE_BOX;
        ctx.strokeStyle = PALETTE.ARTICLE_BORDER;
        ctx.lineWidth = 1;
        drawRoundedRect(
          ctx,
          articleBoxDrawX,
          articleBoxDrawY,
          articleBoxWidth,
          ARTICLE_BOX_HEIGHT,
          ARTICLE_BOX_RADIUS,
          true,
          true,
        );

        if (article.type === "highlight") {
          ctx.strokeStyle = PALETTE.HIGHLIGHT_ARTICLE_HOVER_BORDER;
          ctx.lineWidth = 1;
          drawRoundedRect(
            ctx,
            articleBoxDrawX,
            articleBoxDrawY,
            articleBoxWidth,
            ARTICLE_BOX_HEIGHT,
            ARTICLE_BOX_RADIUS,
            false,
            true,
          );
        }

        ctx.fillStyle =
          article.type === "highlight"
            ? PALETTE.ACCENT_HIGHLIGHT_TEXT
            : PALETTE.TEXT_PRIMARY;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(
          article.text,
          articleBoxDrawX + ARTICLE_BOX_PADDING_X,
          articleBoxDrawY + ARTICLE_BOX_PADDING_Y,
        );

        const markerCenterXOnScreen =
          timelineTrackScreenStartX +
          scrollOffsetX +
          articleCenterPixelXOffsetFromOrigin;
        ctx.beginPath();
        ctx.arc(
          markerCenterXOnScreen,
          timelineLineYScrolled,
          ARTICLE_MARKER_RADIUS,
          0,
          Math.PI * 2,
        );
        ctx.fillStyle =
          article.type === "highlight"
            ? PALETTE.HIGHLIGHT_ARTICLE_HOVER_BORDER
            : PALETTE.MARKER_FILL;
        ctx.fill();
      });
    });

    hoveredArticles.forEach((detail) => {
      const article = detail.articleRef;
      const playerRowTopUnscrolled =
        M_AREA_Y + detail.playerIndex * PLAYER_ROW_HEIGHT;
      const timelineLineYScrolled =
        playerRowTopUnscrolled + scrollOffsetY + TIMELINE_Y_OFFSET_IN_ROW;
      const articleBoxActualYScrolled =
        timelineLineYScrolled -
        ARTICLE_MARKER_RADIUS -
        ARTICLE_BOX_BOTTOM_MARGIN_TO_MARKER -
        ARTICLE_BOX_HEIGHT;

      ctx.font = ARTICLE_TEXT_FONT;
      const textMetrics = ctx.measureText(article.text);
      const articleBoxWidth = textMetrics.width + 2 * ARTICLE_BOX_PADDING_X;
      const articleCenterPixelXOffsetFromOrigin =
        article.duration * pixelsPerSecond;
      const articleBoxDrawX =
        M_AREA_X +
        scrollOffsetX +
        articleCenterPixelXOffsetFromOrigin -
        articleBoxWidth / 2;
      const articleBoxDrawY = articleBoxActualYScrolled;

      if (!(
        articleBoxDrawX + articleBoxWidth < M_AREA_X ||
        articleBoxDrawX > M_AREA_X + M_AREA_W ||
        articleBoxDrawY + ARTICLE_BOX_HEIGHT < M_AREA_Y ||
        articleBoxDrawY > M_AREA_Y + mainPanelContentHeightForPlayerRows
      )) {
        const hoverBg =
          article.type === "highlight"
            ? PALETTE.HIGHLIGHT_ARTICLE_HOVER_BG
            : PALETTE.ARTICLE_HOVER_BG;
        const hoverBorder =
          article.type === "highlight"
            ? PALETTE.HIGHLIGHT_ARTICLE_HOVER_BORDER
            : PALETTE.ARTICLE_HOVER_BORDER;
        const hoverText =
          article.type === "highlight"
            ? PALETTE.HIGHLIGHT_ARTICLE_HOVER_TEXT
            : PALETTE.ARTICLE_HOVER_TEXT;

        ctx.lineWidth = 1.5;
        ctx.strokeStyle = hoverBorder;
        ctx.fillStyle = hoverBg;

        drawRoundedRect(
          ctx,
          articleBoxDrawX,
          articleBoxDrawY,
          articleBoxWidth,
          ARTICLE_BOX_HEIGHT,
          ARTICLE_BOX_RADIUS,
          true,
          true,
        );

        ctx.fillStyle = hoverText;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(
          article.text,
          articleBoxDrawX + ARTICLE_BOX_PADDING_X,
          articleBoxDrawY + ARTICLE_BOX_PADDING_Y,
        );

        const markerCenterXOnScreen =
          M_AREA_X + scrollOffsetX + articleCenterPixelXOffsetFromOrigin;
        ctx.beginPath();
        ctx.arc(
          markerCenterXOnScreen,
          timelineLineYScrolled,
          ARTICLE_MARKER_RADIUS + 1,
          0,
          Math.PI * 2,
        );
        ctx.fillStyle = hoverBorder;
        ctx.fill();

        ctx.font = TIME_AXIS_FONT;
        ctx.fillStyle = PALETTE.TEXT_PRIMARY;
        const timestampText = formatDuration(article.duration);
        const timestampTextMetrics = ctx.measureText(timestampText);
        const timestampX =
          markerCenterXOnScreen - timestampTextMetrics.width / 2;
        const timestampY = timelineLineYScrolled + ARTICLE_MARKER_RADIUS + 15;
        ctx.fillText(timestampText, timestampX, timestampY);
      }
    });
    ctx.restore();

    const timeAxisYStart = M_AREA_Y + mainPanelContentHeightForPlayerRows;
    ctx.save();
    ctx.beginPath();
    ctx.rect(M_AREA_X, timeAxisYStart, M_AREA_W, TIME_AXIS_AREA_HEIGHT);
    ctx.clip();

    const timeAxisBaseY = timeAxisYStart + TIME_AXIS_PADDING_TOP_INTERNAL;
    const timeAxisLineY = timeAxisBaseY + TIME_AXIS_MAJOR_TICK_HEIGHT / 2;

    ctx.strokeStyle = PALETTE.TIME_AXIS_LINE;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(timelineTrackScreenStartX, timeAxisLineY);
    ctx.lineTo(timelineTrackScreenEndX, timeAxisLineY);
    ctx.stroke();

    ctx.fillStyle = PALETTE.TIME_AXIS_TEXT;
    ctx.font = TIME_AXIS_FONT;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const scaleConfig = getTimeScaleConfig(pixelsPerSecond);
    const majorTickIntervalSeconds = scaleConfig.majorInterval;
    const minorTicksPerMajorInterval = scaleConfig.minorTicks;
    const timeUnitSuffix = scaleConfig.suffix;
    const labelDivisor = scaleConfig.divisor;

    const minorTickIntervalSeconds =
      majorTickIntervalSeconds / minorTicksPerMajorInterval;
    const timeInSecondsAtScreenStart = -scrollOffsetX / pixelsPerSecond;
    const timeInSecondsAtScreenEnd =
      (-scrollOffsetX + M_AREA_W) / pixelsPerSecond;

    let currentTimeInSeconds =
      Math.floor(timeInSecondsAtScreenStart / majorTickIntervalSeconds) *
      majorTickIntervalSeconds;

    while (
      currentTimeInSeconds <=
      timeInSecondsAtScreenEnd + majorTickIntervalSeconds
    ) {
      const tickScreenX =
        timelineTrackScreenStartX +
        currentTimeInSeconds * pixelsPerSecond +
        scrollOffsetX;

      if (
        tickScreenX >= timelineTrackScreenStartX &&
        tickScreenX <= timelineTrackScreenEndX
      ) {
        ctx.beginPath();
        ctx.moveTo(
          tickScreenX,
          timeAxisLineY - TIME_AXIS_MAJOR_TICK_HEIGHT / 2,
        );
        ctx.lineTo(
          tickScreenX,
          timeAxisLineY + TIME_AXIS_MAJOR_TICK_HEIGHT / 2,
        );
        ctx.stroke();
      }

      const labelValue = Math.round(currentTimeInSeconds / labelDivisor);
      const labelText = labelValue + timeUnitSuffix;
      const labelWidth = ctx.measureText(labelText).width;
      let labelDrawX = tickScreenX;

      if (tickScreenX - labelWidth / 2 < timelineTrackScreenStartX) {
        labelDrawX = timelineTrackScreenStartX + labelWidth / 2 + 2;
      } else if (tickScreenX + labelWidth / 2 > timelineTrackScreenEndX) {
        labelDrawX = timelineTrackScreenEndX - labelWidth / 2 - 2;
      }

      if (
        tickScreenX >= timelineTrackScreenStartX - labelWidth &&
        tickScreenX <= timelineTrackScreenEndX + labelWidth
      ) {
        ctx.fillText(
          labelText,
          labelDrawX,
          timeAxisLineY +
            TIME_AXIS_MAJOR_TICK_HEIGHT / 2 +
            TIME_AXIS_TEXT_OFFSET_Y,
        );
      }

      for (let m = 1; m < minorTicksPerMajorInterval; m++) {
        const minorTimeInSeconds =
          currentTimeInSeconds + m * minorTickIntervalSeconds;
        const minorTickScreenX =
          timelineTrackScreenStartX +
          minorTimeInSeconds * pixelsPerSecond +
          scrollOffsetX;

        if (
          minorTickScreenX > timelineTrackScreenStartX &&
          minorTickScreenX < timelineTrackScreenEndX
        ) {
          ctx.beginPath();
          ctx.moveTo(
            minorTickScreenX,
            timeAxisLineY - TIME_AXIS_MINOR_TICK_HEIGHT / 2,
          );
          ctx.lineTo(
            minorTickScreenX,
            timeAxisLineY + TIME_AXIS_MINOR_TICK_HEIGHT / 2,
          );
          ctx.stroke();
        }
      }
      currentTimeInSeconds += majorTickIntervalSeconds;
    }
    ctx.restore();

    const bounds = calculateBounds();
    const scrollbarAreaY =
      M_AREA_Y + mainPanelContentHeightForPlayerRows + TIME_AXIS_AREA_HEIGHT;
    const SCROLLBAR_TRACK_X = M_AREA_X;
    const SCROLLBAR_TRACK_Y =
      scrollbarAreaY +
      (HORIZONTAL_SCROLLBAR_AREA_HEIGHT - HORIZONTAL_SCROLLBAR_HEIGHT) / 2;
    const SCROLLBAR_TRACK_W = M_AREA_W;
    const SCROLLBAR_TRACK_H = HORIZONTAL_SCROLLBAR_HEIGHT;

    if (bounds.contentSpanTotalPixels > M_AREA_W) {
      ctx.fillStyle = PALETTE.SCROLLBAR_BG;
      drawRoundedRect(
        ctx,
        SCROLLBAR_TRACK_X,
        SCROLLBAR_TRACK_Y,
        SCROLLBAR_TRACK_W,
        SCROLLBAR_TRACK_H,
        HORIZONTAL_SCROLLBAR_RADIUS,
        true,
        false,
      );

      const viewableRatio = M_AREA_W / bounds.contentSpanTotalPixels;
      const SCROLLBAR_THUMB_W = Math.max(20, SCROLLBAR_TRACK_W * viewableRatio);

      const scrollableContentWidth = bounds.contentSpanTotalPixels - M_AREA_W;
      if (scrollableContentWidth > 0) {
        const currentScrolledAmount = -bounds.minContentStartPx - scrollOffsetX;
        const scrollRatio = currentScrolledAmount / scrollableContentWidth;

        const SCROLLBAR_THUMB_X =
          SCROLLBAR_TRACK_X +
          scrollRatio * (SCROLLBAR_TRACK_W - SCROLLBAR_THUMB_W);
        const SCROLLBAR_THUMB_Y = SCROLLBAR_TRACK_Y;

        ctx.fillStyle = isMouseOverScrollbarThumbRef.current
          ? PALETTE.SCROLLBAR_THUMB_HOVER
          : PALETTE.SCROLLBAR_THUMB;
        drawRoundedRect(
          ctx,
          SCROLLBAR_THUMB_X,
          SCROLLBAR_THUMB_Y,
          SCROLLBAR_THUMB_W,
          HORIZONTAL_SCROLLBAR_HEIGHT,
          HORIZONTAL_SCROLLBAR_RADIUS,
          true,
          false,
        );
      }
    }

    ctx.restore();
  }, [
    players,
    scrollOffsetX,
    scrollOffsetY,
    pixelsPerSecond,
    hoveredArticles,
    PALETTE,
    calculateBounds,
  ]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const dpr = window.devicePixelRatio || 1;
    const displayWidth = container.clientWidth;
    const displayHeight = container.clientHeight;

    canvas.style.width = displayWidth + "px";
    canvas.style.height = displayHeight + "px";
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;

    if (!initialZoomSetRef.current && displayWidth > 0) {
      const M_AREA_X = PADDING + SIDEBAR_WIDTH + PADDING;
      const M_AREA_W = displayWidth - M_AREA_X - PADDING;
      const visibleSeconds = 180;
      setPixelsPerSecond(M_AREA_W / visibleSeconds);
      initialZoomSetRef.current = true;
    }

    const ctx = canvas.getContext("2d");
    if (
      ctx &&
      players.length > 0 &&
      players[0].articles.length > 0 &&
      scrollOffsetX === 0
    ) {
      ctx.font = ARTICLE_TEXT_FONT;
      const textMetrics = ctx.measureText(players[0].articles[0].text);
      const firstArticleWidth = textMetrics.width + 2 * ARTICLE_BOX_PADDING_X;
      setScrollOffsetX(firstArticleWidth / 2);
    }

    draw();
  }, [draw, players, scrollOffsetX]);

  useEffect(() => {
    resizeCanvas();
    const container = containerRef.current;
    const resizeObserver = container
      ? new ResizeObserver(resizeCanvas)
      : undefined;

    if (container) resizeObserver?.observe(container);
    return () => resizeObserver?.disconnect();
  }, [resizeCanvas]);

  useEffect(() => {
    draw();
  }, [draw]);

  const findHoveredArticle = useCallback(
    (canvasX: number, canvasY: number): HoveredArticle | null => {
      if (!canvasRef.current || players.length === 0) return null;

      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      const logicalWidth = canvas.width / dpr;
      const logicalHeight = canvas.height / dpr;

      const M_AREA_X = PADDING + SIDEBAR_WIDTH + PADDING;
      const M_AREA_Y = PADDING;
      const M_AREA_H = logicalHeight - 2 * PADDING;
      const mainPanelContentClipHeight =
        M_AREA_H - TIME_AXIS_AREA_HEIGHT - HORIZONTAL_SCROLLBAR_AREA_HEIGHT;

      if (
        canvasY < M_AREA_Y ||
        canvasY > M_AREA_Y + mainPanelContentClipHeight
      ) {
        return null;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.font = ARTICLE_TEXT_FONT;

      let closestDotCandidate: {
        playerIndex: number;
        articleIndex: number;
        articleRef: TimelineItem;
        distance: number;
      } | null = null;
      const cardCandidates: {
        playerIndex: number;
        articleIndex: number;
        articleRef: TimelineItem;
        distance: number;
      }[] = [];

      for (let playerIndex = 0; playerIndex < players.length; playerIndex++) {
        const player = players[playerIndex];
        const playerRowTopUnscrolled =
          M_AREA_Y + playerIndex * PLAYER_ROW_HEIGHT;
        const playerRowTopScrolled = playerRowTopUnscrolled + scrollOffsetY;
        const timelineLineYScrolled =
          playerRowTopScrolled + TIMELINE_Y_OFFSET_IN_ROW;

        if (
          playerRowTopScrolled + PLAYER_ROW_HEIGHT < M_AREA_Y ||
          playerRowTopScrolled > M_AREA_Y + mainPanelContentClipHeight
        ) {
          continue;
        }

        for (
          let articleIndex = 0;
          articleIndex < player.articles.length;
          articleIndex++
        ) {
          const article = player.articles[articleIndex];
          const articleCenterPixelXOffsetFromOrigin =
            article.duration * pixelsPerSecond;
          const markerCenterXOnScreen =
            M_AREA_X + scrollOffsetX + articleCenterPixelXOffsetFromOrigin;

          const distanceToDot = Math.sqrt(
            Math.pow(canvasX - markerCenterXOnScreen, 2) +
              Math.pow(canvasY - timelineLineYScrolled, 2),
          );
          if (distanceToDot <= ARTICLE_MARKER_RADIUS + 0.5) {
            if (
              !closestDotCandidate ||
              distanceToDot < closestDotCandidate.distance
            ) {
              closestDotCandidate = {
                playerIndex,
                articleIndex,
                articleRef: article,
                distance: distanceToDot,
              };
            }
          }

          const textMetrics = ctx.measureText(article.text);
          const articleBoxWidth = textMetrics.width + 2 * ARTICLE_BOX_PADDING_X;
          const articleBoxCalculatedY =
            timelineLineYScrolled -
            ARTICLE_MARKER_RADIUS -
            ARTICLE_BOX_BOTTOM_MARGIN_TO_MARKER -
            ARTICLE_BOX_HEIGHT;
          const articleBoxLeftScreenX =
            M_AREA_X +
            scrollOffsetX +
            articleCenterPixelXOffsetFromOrigin -
            articleBoxWidth / 2;
          const articleBoxCenterX = articleBoxLeftScreenX + articleBoxWidth / 2;
          const articleBoxCenterY =
            articleBoxCalculatedY + ARTICLE_BOX_HEIGHT / 2;

          const distanceToCenter = Math.sqrt(
            Math.pow(canvasX - articleBoxCenterX, 2) +
              Math.pow(canvasY - articleBoxCenterY, 2),
          );

          if (
            canvasX >= articleBoxLeftScreenX &&
            canvasX <= articleBoxLeftScreenX + articleBoxWidth &&
            canvasY >= articleBoxCalculatedY &&
            canvasY <= articleBoxCalculatedY + ARTICLE_BOX_HEIGHT
          ) {
            if (
              articleBoxLeftScreenX + articleBoxWidth < M_AREA_X ||
              articleBoxLeftScreenX >
                M_AREA_X + logicalWidth - PADDING - SIDEBAR_WIDTH - PADDING
            ) {
              continue;
            }
            cardCandidates.push({
              playerIndex,
              articleIndex,
              articleRef: article,
              distance: distanceToCenter,
            });
          }
        }
      }

      if (closestDotCandidate) {
        return {
          playerIndex: closestDotCandidate.playerIndex,
          articleIndex: closestDotCandidate.articleIndex,
          articleRef: closestDotCandidate.articleRef,
        };
      }

      if (cardCandidates.length === 0) {
        return null;
      }

      cardCandidates.sort((a, b) => a.distance - b.distance);
      return {
        playerIndex: cardCandidates[0].playerIndex,
        articleIndex: cardCandidates[0].articleIndex,
        articleRef: cardCandidates[0].articleRef,
      };
    },
    [players, scrollOffsetY, scrollOffsetX, pixelsPerSecond],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      lastMouseRef.current = { x, y };

      if (isDragging) return;

      const bounds = calculateBounds();
      const dpr = window.devicePixelRatio || 1;
      const logicalWidth = canvas.width / dpr;
      const logicalHeight = canvas.height / dpr;
      const M_AREA_X = PADDING + SIDEBAR_WIDTH + PADDING;
      const M_AREA_Y = PADDING;
      const M_AREA_W = logicalWidth - M_AREA_X - PADDING;
      const M_AREA_H = logicalHeight - 2 * PADDING;
      const mainPanelContentHeight =
        M_AREA_H - TIME_AXIS_AREA_HEIGHT - HORIZONTAL_SCROLLBAR_AREA_HEIGHT;
      const scrollbarAreaY =
        M_AREA_Y + mainPanelContentHeight + TIME_AXIS_AREA_HEIGHT;
      const SCROLLBAR_TRACK_Y =
        scrollbarAreaY +
        (HORIZONTAL_SCROLLBAR_AREA_HEIGHT - HORIZONTAL_SCROLLBAR_HEIGHT) / 2;

      const viewableRatio = M_AREA_W / bounds.contentSpanTotalPixels;
      const SCROLLBAR_THUMB_W = Math.max(20, M_AREA_W * viewableRatio);
      const scrollableContentWidth = bounds.contentSpanTotalPixels - M_AREA_W;
      const currentScrolledAmount = -bounds.minContentStartPx - scrollOffsetX;
      const scrollRatio = currentScrolledAmount / scrollableContentWidth;
      const SCROLLBAR_THUMB_X =
        M_AREA_X + scrollRatio * (M_AREA_W - SCROLLBAR_THUMB_W);

      isMouseOverScrollbarThumbRef.current =
        bounds.contentSpanTotalPixels > M_AREA_W &&
        x >= SCROLLBAR_THUMB_X &&
        x <= SCROLLBAR_THUMB_X + SCROLLBAR_THUMB_W &&
        y >= SCROLLBAR_TRACK_Y &&
        y <= SCROLLBAR_TRACK_Y + HORIZONTAL_SCROLLBAR_HEIGHT;

      const primaryHoveredArticle = findHoveredArticle(x, y);
      const newHoveredArticles: HoveredArticle[] = [];

      if (primaryHoveredArticle) {
        newHoveredArticles.push(primaryHoveredArticle);
        const crosshairTime = primaryHoveredArticle.articleRef.duration;

        players.forEach((player, playerIndex) => {
          if (playerIndex === primaryHoveredArticle.playerIndex) return;

          let bestArticle: HoveredArticle | null = null;
          for (let i = player.articles.length - 1; i >= 0; i--) {
            const article = player.articles[i];
            if (article.duration <= crosshairTime) {
              bestArticle = {
                playerIndex,
                articleIndex: i,
                articleRef: article,
              };
              break;
            }
          }
          if (bestArticle) {
            newHoveredArticles.push(bestArticle);
          }
        });
      }

      setHoveredArticles(newHoveredArticles);

      let newCursor = "default";
      if (newHoveredArticles.length > 0) {
        newCursor = "pointer";
      } else if (isMouseOverScrollbarThumbRef.current) {
        newCursor = "grab";
      } else if (
        x >= M_AREA_X &&
        x <= logicalWidth - PADDING &&
        y >= M_AREA_Y &&
        y <= M_AREA_Y + mainPanelContentHeight
      ) {
        newCursor = "grab";
      }

      canvas.style.cursor = newCursor;
    },
    [isDragging, calculateBounds, scrollOffsetX, findHoveredArticle, players],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (e.button !== 0 || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const bounds = calculateBounds();
      const dpr = window.devicePixelRatio || 1;
      const logicalWidth = canvas.width / dpr;
      const logicalHeight = canvas.height / dpr;
      const S_AREA_X = PADDING;
      const S_AREA_Y = PADDING;
      const S_AREA_W = SIDEBAR_WIDTH;
      const S_AREA_H = logicalHeight - 2 * PADDING;
      const M_AREA_X = PADDING + SIDEBAR_WIDTH + PADDING;
      const M_AREA_Y = PADDING;
      const M_AREA_W = logicalWidth - M_AREA_X - PADDING;
      const M_AREA_H = logicalHeight - 2 * PADDING;
      const mainPanelContentHeight =
        M_AREA_H - TIME_AXIS_AREA_HEIGHT - HORIZONTAL_SCROLLBAR_AREA_HEIGHT;
      const scrollbarAreaY =
        M_AREA_Y + mainPanelContentHeight + TIME_AXIS_AREA_HEIGHT;

      const viewableRatio = M_AREA_W / bounds.contentSpanTotalPixels;
      const SCROLLBAR_THUMB_W = Math.max(20, M_AREA_W * viewableRatio);
      const scrollableContentWidth = bounds.contentSpanTotalPixels - M_AREA_W;
      const currentScrolledAmount = -bounds.minContentStartPx - scrollOffsetX;
      const scrollRatio = currentScrolledAmount / scrollableContentWidth;
      const SCROLLBAR_THUMB_X =
        M_AREA_X + scrollRatio * (M_AREA_W - SCROLLBAR_THUMB_W);
      const SCROLLBAR_TRACK_Y =
        scrollbarAreaY +
        (HORIZONTAL_SCROLLBAR_AREA_HEIGHT - HORIZONTAL_SCROLLBAR_HEIGHT) / 2;

      const isOverScrollbarThumb =
        bounds.contentSpanTotalPixels > M_AREA_W &&
        x >= SCROLLBAR_THUMB_X &&
        x <= SCROLLBAR_THUMB_X + SCROLLBAR_THUMB_W &&
        y >= SCROLLBAR_TRACK_Y &&
        y <= SCROLLBAR_TRACK_Y + HORIZONTAL_SCROLLBAR_HEIGHT;

      if (isOverScrollbarThumb) {
        setIsDragging(true);
        setDragType("scrollbar");
        dragStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          scrollX: scrollOffsetX,
          scrollY: scrollOffsetY,
          thumbStartX: x - SCROLLBAR_THUMB_X,
        };
      } else if (
        x >= M_AREA_X &&
        x <= logicalWidth - PADDING &&
        y >= M_AREA_Y &&
        y <= M_AREA_Y + mainPanelContentHeight
      ) {
        setIsDragging(true);
        setDragType("main");
        dragStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          scrollX: scrollOffsetX,
          scrollY: scrollOffsetY,
          thumbStartX: 0,
        };
      } else if (
        x >= S_AREA_X &&
        x <= S_AREA_X + S_AREA_W &&
        y >= S_AREA_Y &&
        y <= S_AREA_Y + S_AREA_H
      ) {
        setIsDragging(true);
        setDragType("sidebar");
        dragStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          scrollX: scrollOffsetX,
          scrollY: scrollOffsetY,
          thumbStartX: 0,
        };
      }

      if (isDragging) {
        canvas.style.cursor = "grabbing";
      }
    },
    [calculateBounds, scrollOffsetX, scrollOffsetY, isDragging],
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleWindowMouseMove = (e: MouseEvent) => {
      const bounds = calculateBounds();
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      if (dragType === "main") {
        const newX = dragStartRef.current.scrollX + deltaX;
        const newY = dragStartRef.current.scrollY + deltaY;
        setScrollOffsetX(
          Math.max(
            bounds.timelineMinScrollOffsetX,
            Math.min(bounds.timelineMaxScrollOffsetX, newX),
          ),
        );
        setScrollOffsetY(
          Math.max(bounds.maxVerticalScrollOffset, Math.min(0, newY)),
        );
      } else if (dragType === "sidebar") {
        const newY = dragStartRef.current.scrollY + deltaY;
        setScrollOffsetY(
          Math.max(bounds.maxVerticalScrollOffset, Math.min(0, newY)),
        );
      } else if (dragType === "scrollbar" && canvasRef.current) {
        const canvas = canvasRef.current;
        const dpr = window.devicePixelRatio || 1;
        const logicalWidth = canvas.width / dpr;
        const M_AREA_X = PADDING + SIDEBAR_WIDTH + PADDING;
        const M_AREA_W = logicalWidth - M_AREA_X - PADDING;
        const SCROLLBAR_TRACK_W = M_AREA_W;

        const viewableRatio = M_AREA_W / bounds.contentSpanTotalPixels;
        const SCROLLBAR_THUMB_W = Math.max(20, M_AREA_W * viewableRatio);
        const trackClickableWidth = SCROLLBAR_TRACK_W - SCROLLBAR_THUMB_W;

        if (trackClickableWidth <= 0) return;

        const rect = canvas.getBoundingClientRect();
        const newThumbScreenX =
          e.clientX - rect.left - dragStartRef.current.thumbStartX;
        let newScrollRatio = (newThumbScreenX - M_AREA_X) / trackClickableWidth;
        newScrollRatio = Math.max(0, Math.min(1, newScrollRatio));

        const scrollableContentWidth = bounds.contentSpanTotalPixels - M_AREA_W;
        if (scrollableContentWidth > 0) {
          const newScrolledAmount = newScrollRatio * scrollableContentWidth;
          setScrollOffsetX(-bounds.minContentStartPx - newScrolledAmount);
        }
      }
    };

    const handleWindowMouseUp = () => {
      setIsDragging(false);
      setDragType(null);
    };

    window.addEventListener("mousemove", handleWindowMouseMove);
    window.addEventListener("mouseup", handleWindowMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleWindowMouseMove);
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };
  }, [isDragging, dragType, calculateBounds]);

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLCanvasElement>) => {
      if (!canvasRef.current) return;
      e.preventDefault();

      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      const logicalWidth = canvas.width / dpr;
      const logicalHeight = canvas.height / dpr;
      const bounds = calculateBounds();

      const M_AREA_X = PADDING + SIDEBAR_WIDTH + PADDING;
      const M_AREA_Y = PADDING;
      const M_AREA_H = logicalHeight - 2 * PADDING;
      const S_AREA_X = PADDING;
      const S_AREA_Y = PADDING;
      const S_AREA_W = SIDEBAR_WIDTH;
      const S_AREA_H = logicalHeight - 2 * PADDING;
      const mainPanelContentHeight =
        M_AREA_H - TIME_AXIS_AREA_HEIGHT - HORIZONTAL_SCROLLBAR_AREA_HEIGHT;

      let dX = e.deltaX;
      let dY = e.deltaY;

      const LINE_SCROLL_MULTIPLIER = 20;
      const PAGE_SCROLL_MULTIPLIER_X =
        (logicalWidth - PADDING - M_AREA_X) * 0.8;
      const PAGE_SCROLL_MULTIPLIER_Y = M_AREA_H * 0.8;

      if (e.deltaMode === 1) {
        dX *= LINE_SCROLL_MULTIPLIER;
        dY *= LINE_SCROLL_MULTIPLIER;
      } else if (e.deltaMode === 2) {
        dX *= PAGE_SCROLL_MULTIPLIER_X;
        dY *= PAGE_SCROLL_MULTIPLIER_Y;
      }

      const isOverMainTimelineArea =
        e.nativeEvent.offsetX >= M_AREA_X &&
        e.nativeEvent.offsetX <= logicalWidth - PADDING &&
        e.nativeEvent.offsetY >= M_AREA_Y &&
        e.nativeEvent.offsetY <= M_AREA_Y + mainPanelContentHeight;
      const isOverSidebarArea =
        e.nativeEvent.offsetX >= S_AREA_X &&
        e.nativeEvent.offsetX <= S_AREA_X + S_AREA_W &&
        e.nativeEvent.offsetY >= S_AREA_Y &&
        e.nativeEvent.offsetY <= S_AREA_Y + S_AREA_H;

      const scrollSensitivityFactor = 0.75;

      if (isOverMainTimelineArea) {
        if (dY !== 0) {
          setScrollOffsetX((prev) =>
            Math.max(
              bounds.timelineMinScrollOffsetX,
              Math.min(
                bounds.timelineMaxScrollOffsetX,
                prev + dY * scrollSensitivityFactor,
              ),
            ),
          );
        }
        if (dX !== 0) {
          setScrollOffsetX((prev) =>
            Math.max(
              bounds.timelineMinScrollOffsetX,
              Math.min(
                bounds.timelineMaxScrollOffsetX,
                prev - dX * scrollSensitivityFactor,
              ),
            ),
          );
        }
      } else if (isOverSidebarArea) {
        if (dY !== 0) {
          setScrollOffsetY((prev) =>
            Math.max(bounds.maxVerticalScrollOffset, Math.min(0, prev - dY)),
          );
        }
      } else {
        if (dY !== 0) {
          setScrollOffsetY((prev) =>
            Math.max(bounds.maxVerticalScrollOffset, Math.min(0, prev - dY)),
          );
        }
        if (dX !== 0) {
          setScrollOffsetX((prev) =>
            Math.max(
              bounds.timelineMinScrollOffsetX,
              Math.min(
                bounds.timelineMaxScrollOffsetX,
                prev - dX * scrollSensitivityFactor,
              ),
            ),
          );
        }
      }
    },
    [calculateBounds],
  );

  const zoomIn = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const logicalWidth = canvas.width / dpr;
    const M_AREA_X = PADDING + SIDEBAR_WIDTH + PADDING;
    const M_AREA_W = logicalWidth - M_AREA_X - PADDING;

    const viewCenterXSeconds =
      (-scrollOffsetX + M_AREA_W / 2) / pixelsPerSecond;
    const newPPS = pixelsPerSecond * ZOOM_FACTOR;

    setPixelsPerSecond(newPPS);
    setScrollOffsetX(-(viewCenterXSeconds * newPPS - M_AREA_W / 2));
  }, [pixelsPerSecond, scrollOffsetX]);

  const zoomOut = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const logicalWidth = canvas.width / dpr;
    const M_AREA_X = PADDING + SIDEBAR_WIDTH + PADDING;
    const M_AREA_W = logicalWidth - M_AREA_X - PADDING;

    const viewCenterXSeconds =
      (-scrollOffsetX + M_AREA_W / 2) / pixelsPerSecond;
    let newPPS = pixelsPerSecond / ZOOM_FACTOR;
    if (newPPS < MIN_PIXELS_PER_SECOND) newPPS = MIN_PIXELS_PER_SECOND;

    setPixelsPerSecond(newPPS);
    setScrollOffsetX(-(viewCenterXSeconds * newPPS - M_AREA_W / 2));
  }, [pixelsPerSecond, scrollOffsetX]);

  const handleMouseLeave = useCallback(() => {
    setHoveredArticles([]);
    isMouseOverScrollbarThumbRef.current = false;
  }, []);

  return (
    <div
      ref={fullscreenRef}
      data-testid="timeline-fullscreen-container"
      className={`w-full ${isFullscreen ? `${theme.bg} flex h-dvh flex-col p-4` : ""}`}
    >
      <MobileTimeline players={players} isDark={isDark} />

      <div className="mb-3 hidden shrink-0 flex-wrap justify-center gap-2 sm:flex">
        <button
          type="button"
          onClick={zoomIn}
          aria-label={t("components.timeline.zoomIn")}
          title={t("components.timeline.zoomIn")}
          data-testid="timeline-zoom-in"
          className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${theme.border} ${theme.buttonSecondary} ${theme.buttonSecondaryText}`}
        >
          <ZoomInIcon />
          {t("components.timeline.zoomIn")}
        </button>
        <button
          type="button"
          onClick={zoomOut}
          aria-label={t("components.timeline.zoomOut")}
          title={t("components.timeline.zoomOut")}
          data-testid="timeline-zoom-out"
          className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${theme.border} ${theme.buttonSecondary} ${theme.buttonSecondaryText}`}
        >
          <ZoomOutIcon />
          {t("components.timeline.zoomOut")}
        </button>
        <FullscreenButton
          isDark={isDark}
          isFullscreen={isFullscreen}
          onClick={() => void toggleFullscreen()}
          testId="timeline-fullscreen-toggle"
        />
      </div>
      <div
        ref={containerRef}
        className={`hidden w-full overflow-hidden rounded-xl border sm:block ${theme.border} ${theme.bgSecondary} ${isFullscreen ? "min-h-0 flex-1" : "h-80"}`}
      >
        <canvas
          ref={canvasRef}
          aria-label={t("components.timeline.label")}
          data-testid="timeline-canvas"
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onWheel={handleWheel}
          className="block"
        />
      </div>
    </div>
  );
}

function ZoomInIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="m21 21-4.35-4.35M11 8v6m3-3H8m10 0a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
      />
    </svg>
  );
}

function ZoomOutIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="m21 21-4.35-4.35M14 11H8m10 0a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
      />
    </svg>
  );
}

export const Timeline = memo(TimelineComponent);
